import type { RouteRecordRaw } from 'vue-router';
import { CONFIG } from '@/app/config/env';

/** Public billing pages, top level like portalRoutes — the payer lands here
 *  from a Stripe redirect and must not hit any auth guard. */
export const billingPublicRoutes: RouteRecordRaw[] = [
  {
    path: '/billing/thank-you',
    name: 'billing.thank-you',
    component: () => import('./pages/PayResultPage.vue'),
    meta: { title: 'Payment', requiresAuth: false, skipTenantGuard: true },
  },
];

export const centralRoutes: RouteRecordRaw[] = [
  {
    // Console URL prefix is configurable (VITE_CONSOLE_PATH) so the admin
    // login can live at a secret, non-guessable path in production. Internal
    // navigation is by route NAME, so it's unaffected by the prefix.
    path: CONFIG.CONSOLE_PATH,
    component: () => import('./layouts/CentralLayout.vue'),
    children: [
      { path: '', redirect: { name: 'central.dashboard' } },
      {
        path: 'login',
        name: 'central.login',
        component: () => import('./pages/CentralLoginPage.vue'),
        meta: { title: 'Super admin', skipTenantGuard: true, isCentral: true },
      },
      {
        path: 'dashboard',
        name: 'central.dashboard',
        component: () => import('./pages/CentralDashboardPage.vue'),
        meta: {
          title: 'Platform dashboard',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'users',
        name: 'central.users',
        component: () => import('./pages/UsersPage.vue'),
        meta: {
          title: 'Console users',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'registry',
        name: 'central.registry',
        component: () => import('./pages/CentralRegistryPage.vue'),
        meta: {
          title: 'Kosovo registry',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'profile',
        name: 'central.profile',
        component: () => import('./pages/ProfilePage.vue'),
        meta: {
          title: 'My profile',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'settings',
        name: 'central.settings',
        component: () => import('./pages/PlatformSettingsPage.vue'),
        meta: {
          title: 'Platform settings',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'messages',
        name: 'central.messages',
        component: () => import('./pages/MessagesPage.vue'),
        meta: {
          title: 'Messages',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'signups',
        name: 'central.signups',
        component: () => import('./pages/SignupsPage.vue'),
        meta: {
          title: 'Signups',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'plans',
        name: 'central.plans',
        component: () => import('./pages/PlansPage.vue'),
        meta: {
          title: 'Plans',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'news',
        name: 'central.news',
        component: () => import('./pages/NewsPostsPage.vue'),
        meta: { title: 'News', skipTenantGuard: true, isCentral: true, requiresCentralAuth: true },
      },
      {
        path: 'news/new',
        name: 'central.news.new',
        component: () => import('./pages/NewsPostEditorPage.vue'),
        meta: { title: 'News', skipTenantGuard: true, isCentral: true, requiresCentralAuth: true },
      },
      {
        path: 'news/categories',
        name: 'central.news.categories',
        component: () => import('./pages/NewsCategoriesPage.vue'),
        meta: { title: 'News', skipTenantGuard: true, isCentral: true, requiresCentralAuth: true },
      },
      {
        path: 'news/:id/edit',
        name: 'central.news.edit',
        component: () => import('./pages/NewsPostEditorPage.vue'),
        meta: { title: 'News', skipTenantGuard: true, isCentral: true, requiresCentralAuth: true },
      },
      {
        path: 'billing',
        name: 'central.billing',
        component: () => import('./pages/BillingPage.vue'),
        meta: {
          title: 'Billing',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'tenants',
        name: 'central.tenants',
        component: () => import('./pages/TenantsListPage.vue'),
        meta: {
          title: 'Tenants',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'tenants/new',
        name: 'central.tenants.create',
        component: () => import('./pages/TenantEditorPage.vue'),
        meta: {
          title: 'New tenant',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
      {
        path: 'tenants/:id/edit',
        name: 'central.tenants.edit',
        component: () => import('./pages/TenantEditorPage.vue'),
        props: true,
        meta: {
          title: 'Edit tenant',
          skipTenantGuard: true,
          isCentral: true,
          requiresCentralAuth: true,
        },
      },
    ],
  },
];
